import { useFormikContext } from "formik";
import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { translate } from "@utils/helperFunctions";
import { FEModules } from "@utils/moduleList";
import { landUseOptions } from "../setupAssistantUtils";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import type { StartWithWithoutGeneric } from "@typedefs/modulesInterfaces";
import { FieldType } from "@typedefs/modulesInterfaces";
import type { FinalForm } from "../SetupAssistantFinish";

const getLUCBeModules = ({
	landUse_start,
	landUse_w,
	landUse_wo,
}: {
	landUse_start?: number;
	landUse_w?: number;
	landUse_wo?: number;
}) => {
	const startModules =
		landUseOptions.find((landOption) => landOption.id === landUse_start)
			?.derivedModules ?? [];
	const withModules =
		landUseOptions.find((landOption) => landOption.id === landUse_w)
			?.derivedModules ?? [];
	const withoutModules =
		landUseOptions.find((landOption) => landOption.id === landUse_wo)
			?.derivedModules ?? [];
	return {
		startModules,
		withModules,
		withoutModules,
	};
};

export const LucAdditionalInputs = () => {
	const {
		values: { landUse_start, landUse_w, landUse_wo, modulesList },
		setFieldValue,
	} = useFormikContext<FinalForm>();
	const [lucHistory, setLucHistory] = useState<string[]>([]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const { startModules, withModules, withoutModules } = getLUCBeModules({
			landUse_start,
			landUse_w,
			landUse_wo,
		});
		const allModules = [...startModules, ...withModules, ...withoutModules];
		for (const module of allModules) {
			if (!lucHistory.includes(module.toString()))
				setLucHistory((prev) => [...prev, module.toString()]);
			if (!modulesList.includes(`${module}`))
				setFieldValue("modulesList", [...modulesList, `${module}`]);
		}
		const moduleListWithoutLuc = modulesList.filter(
			(module) => module !== FEModules.LandUseChange.toString(),
		);
		if (moduleListWithoutLuc.length > 0 && allModules.length > 0) {
			const moduleListWithoutSelectedLuc = moduleListWithoutLuc.filter(
				(module) => {
					return !allModules.some((lucModuleId) => {
						return lucModuleId.toString() === module.toString();
					});
				},
			);
			const moduleToUnCheck = lucHistory.find((lucModuleHistory) =>
				moduleListWithoutSelectedLuc.some((mod) => mod === lucModuleHistory),
			);
			const newModuleList = modulesList.filter(
				(mod) => mod !== moduleToUnCheck,
			);
			if (moduleListWithoutSelectedLuc.length > 0 && moduleToUnCheck) {
				const newHistory = lucHistory.filter(
					(history) => history !== moduleToUnCheck,
				);
				setLucHistory(newHistory);
				setFieldValue("modulesList", [...newModuleList]);
			}
		}
	}, [landUse_start, landUse_w, landUse_wo, modulesList, setFieldValue]);

	const getTranslatedLandUse = useCallback((): StartWithWithoutGeneric => {
		const translatedOptions = landUseOptions.map((option) => {
			return {
				...option,
				name: translate(option.name),
			};
		})
		return {
			type: FieldType.SWW_SELECT,
			inputName: "landUse",
			label: "module.type_of_land_use",
			startProps: { dropdownOptions: translatedOptions },
			withProps: { dropdownOptions: translatedOptions },
			withoutProps: { dropdownOptions: translatedOptions },
		};
	}, []);

	return (
		<motion.div
			animate={{ opacity: 1, height: "fit-content" }}
			initial={{ opacity: 0, height: 0 }}
			exit={{ opacity: 0, height: 0 }}
		>
			<GenericInputGroup inputGroup={getTranslatedLandUse()} showCommentIcon={false} />
		</motion.div>
	);
}; 